/**
 * EmployeeWeekCellPopup.tsx
 * Popover detail card for a capacity cell click.
 *
 * DAY-scoped by default (2026-06-23, Gigi): clicking a DAY cell opens the detail for
 * THAT day only (the assignments active on it), with a one-click toggle to expand to
 * the whole week. Previously a day-click always showed the entire week, which was
 * confusing. When `clickedDay` is omitted the popup falls back to the week view.
 *
 * Each assignment row is compact: name + a Tentative/Booked status pill on the first
 * line, then the WO / Quote link INLINE with the project name on the second.
 *
 * Cross-scenario SOFT holds (a "held in another plan" day) are now clickable too and
 * render as a distinct tentative block, so an entirely-soft day still opens a detail.
 *
 * Color semantics reference theme.schedule.* / palette tokens. No hex literals.
 */

import React, { useEffect, useState } from 'react';
import {
  Popover,
  Box,
  Typography,
  Chip,
  CircularProgress,
  Alert,
  Divider,
  Button,
  Link,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import CalendarViewWeekIcon from '@mui/icons-material/CalendarViewWeek';
import TodayIcon from '@mui/icons-material/Today';
import { useTheme, alpha } from '@mui/material/styles';
import {
  workingDaysCoveredInWeek,
  workingDaysInWeek,
} from '../../../components/schedule/employeeScheduleShared';
import {
  weekBusyPercent,
  weekPercentFromCell,
  personDayState,
  personDayStateLabel,
  isTaskCountAmber,
} from '../../../utils/personDayDisplay';
import {
  type AssignmentBarDetail,
  type CrossScenarioSource,
  type SourceAllocationDetail,
} from '../../../services/scheduleEmployeeSchedule';
import { allocationBand, allocationBandLabel } from '../../../utils/scheduleAllocationBands';

// ---------------------------------------------------------------------------
// Pure helper — per-source allocation breakdown (D6)
// ---------------------------------------------------------------------------

/**
 * Order a per-source allocation breakdown for display: biggest share first,
 * ties broken by label so the list is stable. Pure (unit-tested); does not
 * mutate the input.
 */
export function orderSourceAllocations(
  list: readonly SourceAllocationDetail[] | undefined | null,
): SourceAllocationDetail[] {
  if (!list || list.length === 0) return [];
  return [...list].sort((a, b) => (b.percent - a.percent) || a.label.localeCompare(b.label));
}

/** Sum of a per-source breakdown's percents (the week's total utilization). */
export function sumSourceAllocations(
  list: readonly SourceAllocationDetail[] | undefined | null,
): number {
  if (!list || list.length === 0) return 0;
  return list.reduce((s, a) => s + (Number.isFinite(a.percent) ? a.percent : 0), 0);
}

// ---------------------------------------------------------------------------
// Pure helper functions (exported for testing)
// ---------------------------------------------------------------------------

/**
 * Build the human-readable label for the source chip.
 *   - sourceType 'Task' + quoteKey    → "Quote {number}"
 *   - sourceType 'Task' + workOrderKey → "WO {number}"
 *   - sourceType 'Task' (neither)      → "Task"
 *   - absence types                    → "Absence: {type}"
 */
export function sourceChipLabel(
  a: Pick<AssignmentBarDetail, 'sourceType' | 'quoteKey' | 'quoteNumber' | 'workOrderKey' | 'workOrderNo'>,
): string {
  if (a.sourceType === 'Task') {
    if (a.quoteKey)    return `Quote ${a.quoteNumber ?? a.quoteKey}`;
    if (a.workOrderKey) return `WO ${a.workOrderNo ?? a.workOrderKey}`;
    return 'Task';
  }
  return `Absence: ${a.sourceType}`;
}

/**
 * Build the navigation path for a clickable source chip (null for absences).
 *   - workOrderKey + projectId → /projects/{projectId}/workorders/{workOrderKey}
 *   - workOrderKey (no project) → /workorders/{workOrderKey}
 *   - quoteKey                 → /quotes/{quoteKey}
 */
export function sourceChipLink(
  a: Pick<AssignmentBarDetail, 'sourceType' | 'quoteKey' | 'workOrderKey' | 'projectId'>,
): string | null {
  if (a.sourceType === 'Task') {
    if (a.quoteKey) return `/quotes/${a.quoteKey}`;
    if (a.workOrderKey) {
      if (a.projectId) return `/projects/${a.projectId}/workorders/${a.workOrderKey}`;
      return `/workorders/${a.workOrderKey}`;
    }
    return null;
  }
  return null;
}

/** Format a week-start Saturday as "Week of Jun 22–26, 2026" (anchored to Monday). */
export function formatWeekLabel(weekStart: string): string {
  const [y, m, d] = weekStart.split('-').map(Number);
  // Closed-popover sentinel ('' — or any unparseable date): return an empty
  // label instead of "Week of Invalid Date–Invalid Date" (v1.0.09 hardening,
  // same bug family as the blank Scenario Planning page).
  if (!Number.isFinite(y) || !Number.isFinite(m) || !Number.isFinite(d)) return '';
  const mon = new Date(Date.UTC(y, m - 1, d + 2));
  const fri = new Date(Date.UTC(y, m - 1, d + 6));
  const monStr = mon.toLocaleDateString('en-CA', { month: 'short', day: 'numeric', timeZone: 'UTC' });
  const friStr = fri.toLocaleDateString('en-CA', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    timeZone: 'UTC',
  });
  return `Week of ${monStr}–${friStr}`;
}

/** Format ISO date as "Oct 14, 2023". */
export function formatDate(iso: string): string {
  const [y, m, d] = iso.split('-').map(Number);
  const dt = new Date(Date.UTC(y, m - 1, d));
  return dt.toLocaleDateString('en-CA', { month: 'short', day: 'numeric', year: 'numeric', timeZone: 'UTC' });
}

/** Format an ISO day as "Mon, Jun 23, 2026" for the day-scoped header. */
export function formatDayLabel(iso: string): string {
  const [y, m, d] = iso.split('-').map(Number);
  const dt = new Date(Date.UTC(y, m - 1, d));
  return dt.toLocaleDateString('en-CA', {
    weekday: 'short',
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    timeZone: 'UTC',
  });
}

/** True when the assignment's [startDate, endDate] covers the given ISO day. */
export function assignmentCoversDay(a: Pick<AssignmentBarDetail, 'startDate' | 'endDate'>, dayISO: string): boolean {
  return a.startDate <= dayISO && a.endDate >= dayISO;
}

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface EmployeeWeekCellPopupProps {
  /** The DOM element the popover is anchored to. Null = closed. */
  anchorEl: HTMLElement | null;
  /** UUID of the user whose cell was clicked. */
  userKey: string;
  /** Display name of the user (for the popup header). */
  userName: string;
  /** ISO Saturday 'YYYY-MM-DD'. */
  weekStart: string;
  /** LEGACY-scale weekly sum — Q5B fallback when busyDays/workingDays absent. */
  totalPercent: number;
  /** Wave-3 additive: busy working days (Q5B numerator) for the header chip. */
  busyDays?: number;
  /** Wave-3 additive: working days in the week (holiday-aware denominator). */
  workingDays?: number;
  /** Assignment list for this user × week. */
  assignments: AssignmentBarDetail[];
  /** True while the detail fetch is in progress. */
  isLoading: boolean;
  /** Error message from detail fetch, if any. */
  error: string | null;
  /** Called to close the popover. */
  onClose: () => void;
  /** Called when "Add Assignment" is clicked. */
  onAddAssignment?: (userKey: string, weekStart: string) => void;
  /** When true, the "Add Assignment" footer is hidden (read-only). */
  readOnly?: boolean;

  // ── Day-scoping (2026-06-23) — optional; when set the popup opens on this DAY ──
  /** ISO day clicked. When set the popup opens day-scoped (toggle to week). */
  clickedDay?: string | null;
  /** LEGACY hard load % on the clicked day (fallback day-state signal). */
  dayPercent?: number;
  /** Wave-3 additive: task count on the clicked day (Q1B chip / state). */
  dayTaskCount?: number;
  /** Wave-3 additive: the clicked day is covered by a dated absence. */
  dayAbsent?: boolean;
  /**
   * Cross-scenario SOFT holds on the clicked day — P2-6: a COUNT of other draft
   * plans holding the day (>0 = held; presence, never a percentage).
   */
  daySoftPercent?: number;
  /** The OTHER draft plans that hold this person (renders the soft block). */
  softSources?: CrossScenarioSource[];
  /** P-22: stat-holiday dates — weekly % (header + rows) divides by real working days. */
  holidays?: ReadonlySet<string>;
  /**
   * Staffing Allocation % (PR B — D5): the person's Σ weekly allocation % for
   * this week (share of their time across all their WO/quote work). Shown as a
   * band line in the header; the per-source breakdown is `sourceAllocations`
   * below. Undefined ⇒ not supplied → the line is hidden.
   */
  weekAllocationPercent?: number;
  /**
   * Staffing Allocation % per-source breakdown (D6) — which WO/quote (or
   * Manual) contributes what % of the week's utilization. Shown as a compact
   * list in WEEK mode; the entries sum to `weekAllocationPercent`. Undefined /
   * empty ⇒ the section is hidden.
   */
  sourceAllocations?: SourceAllocationDetail[];
}

// ---------------------------------------------------------------------------
// Color token for sourceType
// ---------------------------------------------------------------------------

type ScheduleColorKey =
  | 'taskAssignment'
  | 'absenceVacation'
  | 'absenceSick'
  | 'absenceFieldWork'
  | 'absenceOther';

function colorTokenForSource(sourceType: AssignmentBarDetail['sourceType']): ScheduleColorKey {
  switch (sourceType) {
    case 'Task':      return 'taskAssignment';
    case 'Vacation':  return 'absenceVacation';
    case 'Sick':      return 'absenceSick';
    case 'FieldWork': return 'absenceFieldWork';
    case 'Other':     return 'absenceOther';
    default:          return 'taskAssignment';
  }
}

// ---------------------------------------------------------------------------
// Sub-component: status pill (Tentative vs Booked)
// ---------------------------------------------------------------------------

const StatusPill: React.FC<{ status: 'Tentative' | 'Committed' }> = ({ status }) => {
  const theme = useTheme();
  const tentative = status === 'Tentative';
  const color = tentative ? theme.schedule.machineTime : theme.palette.success.main;
  return (
    <Box
      component="span"
      title={tentative ? 'Tentative — proposed in this plan, not finalized to the live schedule' : 'Booked — committed on the live schedule'}
      sx={{
        flexShrink: 0,
        px: 0.5,
        py: 0.05,
        borderRadius: '3px',
        fontSize: '0.54rem',
        fontWeight: 800,
        letterSpacing: '0.04em',
        textTransform: 'uppercase',
        lineHeight: 1.5,
        color,
        backgroundColor: alpha(color, 0.14),
        border: `1px solid ${alpha(color, 0.4)}`,
      }}
    >
      {tentative ? 'Tentative' : 'Booked'}
    </Box>
  );
};

// ---------------------------------------------------------------------------
// Sub-component: assignment list row
// ---------------------------------------------------------------------------

interface AssignmentListRowProps {
  assignment: AssignmentBarDetail;
  /** ISO Saturday — to compute this row's weekly contribution (week view). */
  weekStart: string;
  /** 'day' shows the per-day %, 'week' shows the weekly contribution + ×Nd. */
  mode: 'day' | 'week';
  /** P-22: stat-holiday dates — rows skip holidays so they sum to the header. */
  holidays?: ReadonlySet<string>;
}

const AssignmentListRow: React.FC<AssignmentListRowProps> = ({ assignment, weekStart, mode, holidays }) => {
  const theme = useTheme();
  const colorKey = colorTokenForSource(assignment.sourceType);
  const barColor = theme.schedule[colorKey] as string;

  const label = sourceChipLabel(assignment);
  const link  = sourceChipLink(assignment);
  // Prefer the WO descriptive name; fall back to the project name. Guard blank /
  // literal-"null" values that the demo data carries for some work orders.
  const rawWo = assignment.workOrderName?.trim();
  const context = (rawWo && rawWo.toLowerCase() !== 'null') ? rawWo : (assignment.projectName ?? null);

  // Wave 3 (Q5B): rows read as DAYS. Day view → the row's STATE on that day
  // (busy / day off). Week view → the row's share of the week (working days
  // covered ÷ working days in the week, holiday-aware per P-22).
  const days = weekStart ? workingDaysCoveredInWeek(assignment.startDate, assignment.endDate, weekStart, holidays) : 1;
  const weekDays = weekStart ? workingDaysInWeek(weekStart, holidays) : 5;
  const isAbsence = assignment.sourceType !== 'Task';
  const dayStateText = isAbsence ? 'day off' : 'busy';
  const rowWeekPct = weekBusyPercent(days, weekDays);

  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'flex-start',
        gap: 1,
        py: 0.85,
        borderBottom: `1px solid ${theme.border.color}`,
        '&:last-child': { borderBottom: 'none' },
      }}
    >
      {/* Color swatch */}
      <Box
        sx={{ width: 10, height: 10, borderRadius: '2px', backgroundColor: barColor, flexShrink: 0, mt: 0.55 }}
        aria-hidden="true"
      />

      <Box sx={{ flex: 1, minWidth: 0 }}>
        {/* Line 1 — name + status pill (left), % (right). */}
        <Box sx={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', gap: 1 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, minWidth: 0 }}>
            <Typography
              variant="body2"
              fontWeight={600}
              title={assignment.name}
              sx={{ fontSize: '0.78rem', lineHeight: 1.3, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
            >
              {assignment.name}
            </Typography>
            {assignment.sourceType === 'Task' && <StatusPill status={assignment.status} />}
          </Box>
          <Box sx={{ flexShrink: 0, textAlign: 'right' }}>
            <Typography variant="caption" fontWeight={700} sx={{ fontSize: '0.75rem', display: 'block', lineHeight: 1.1 }}>
              {mode === 'day' ? dayStateText : `${rowWeekPct}%`}
            </Typography>
            {mode === 'week' && (
              <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.6rem', display: 'block', lineHeight: 1.1 }}>
                {dayStateText === 'day off' ? `${days}d off` : `${days} of ${weekDays}d`}
              </Typography>
            )}
          </Box>
        </Box>

        {/* Line 2 — WO / Quote chip INLINE with the project / WO descriptive name. */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mt: 0.4, minWidth: 0 }}>
          {link ? (
            <Chip
              component={Link}
              href={link}
              target="_blank"
              rel="noopener noreferrer"
              label={label}
              size="small"
              icon={<OpenInNewIcon sx={{ fontSize: '0.7rem !important' }} />}
              clickable
              sx={{
                flexShrink: 0,
                height: 20,
                fontSize: '0.62rem',
                backgroundColor: `${barColor}22`,
                color: theme.palette.text.primary,
                border: `1px solid ${barColor}`,
                cursor: 'pointer',
                '& .MuiChip-label': { px: 0.5 },
                '& .MuiChip-icon': { ml: 0.5, mr: '-2px', color: theme.palette.text.secondary },
              }}
            />
          ) : (
            <Chip
              label={label}
              size="small"
              sx={{
                flexShrink: 0,
                height: 20,
                fontSize: '0.62rem',
                backgroundColor: `${barColor}22`,
                color: theme.palette.text.secondary,
                border: `1px solid ${barColor}55`,
                '& .MuiChip-label': { px: 0.5 },
              }}
            />
          )}
          {context && (
            <Typography
              variant="caption"
              color="text.secondary"
              title={context}
              sx={{ fontSize: '0.66rem', minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
            >
              {context}
            </Typography>
          )}
        </Box>

        {/* Line 3 — date range (week view only; in day view the day IS the header). */}
        {mode === 'week' && (
          <Typography variant="caption" color="text.secondary" display="block" sx={{ fontSize: '0.64rem', mt: 0.3 }}>
            {formatDate(assignment.startDate)} – {formatDate(assignment.endDate)}
          </Typography>
        )}
      </Box>
    </Box>
  );
};

// ---------------------------------------------------------------------------
// Sub-component: cross-scenario soft-hold block
// ---------------------------------------------------------------------------

/**
 * P2-6 (audit #2): a soft hold is PRESENCE, not a fraction — the retired stored
 * Percent is never rendered. In day view the clicked day simply reads "held";
 * the plan chips below carry the attribution.
 */
const SoftHoldBlock: React.FC<{ heldToday: boolean; sources: CrossScenarioSource[] }> = ({ heldToday, sources }) => {
  const theme = useTheme();
  const orange = theme.schedule.conflictSoft;
  return (
    <Box
      sx={{
        mt: 1,
        p: 1,
        borderRadius: '6px',
        border: `1px dashed ${alpha(orange, 0.6)}`,
        backgroundColor: alpha(orange, 0.06),
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', gap: 1 }}>
        <Typography variant="caption" fontWeight={700} sx={{ fontSize: '0.7rem', color: orange }}>
          Tentatively held in another plan
        </Typography>
        {heldToday && (
          <Typography variant="caption" fontWeight={700} sx={{ fontSize: '0.72rem', color: orange, flexShrink: 0 }}>
            held this day
          </Typography>
        )}
      </Box>
      <Typography variant="caption" color="text.secondary" display="block" sx={{ fontSize: '0.62rem', mt: 0.25 }}>
        Advisory only — another draft scenario pencils this person in. It never blocks or
        counts against capacity here.
      </Typography>
      {sources.length > 0 && (
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mt: 0.6 }}>
          {sources.map((s) => (
            <Chip
              key={s.scenarioKey}
              label={[s.code, s.name].filter(Boolean).join(' · ') || s.scenarioKey}
              title={s.owner ? `Owned by ${s.owner}` : undefined}
              size="small"
              sx={{
                height: 18,
                fontSize: '0.58rem',
                backgroundColor: alpha(orange, 0.12),
                color: theme.palette.text.secondary,
                border: `1px solid ${alpha(orange, 0.5)}`,
                '& .MuiChip-label': { px: 0.5 },
              }}
            />
          ))}
        </Box>
      )}
    </Box>
  );
};

// ---------------------------------------------------------------------------
// Sub-component: per-source allocation breakdown (D6, week view)
// ---------------------------------------------------------------------------

/**
 * "Allocation by source" — the week's utilization broken down by which WO /
 * quote (or Manual) contributes what %. The rows sum to the header's week
 * allocation %. Shown only in week mode.
 */
const SourceBreakdownBlock: React.FC<{ sources: SourceAllocationDetail[] }> = ({ sources }) => {
  const theme = useTheme();
  const ordered = orderSourceAllocations(sources);
  if (ordered.length === 0) return null;
  return (
    <Box sx={{ mb: 1, p: 1, borderRadius: '6px', backgroundColor: theme.table.stripeBg, border: `1px solid ${theme.border.color}` }}>
      <Typography variant="caption" fontWeight={700} sx={{ fontSize: '0.64rem', textTransform: 'uppercase', letterSpacing: 0.4, color: 'text.secondary', display: 'block', mb: 0.5 }}>
        Allocation by source
      </Typography>
      {ordered.map((s) => {
        const color =
          allocationBand(s.percent) === 'over' ? theme.schedule.allocOver
            : allocationBand(s.percent) === 'at' ? theme.schedule.allocAt
              : allocationBand(s.percent) === 'healthy' ? theme.schedule.allocHealthy
                : theme.schedule.allocUnder;
        return (
          <Box key={`${s.sourceType}:${s.sourceKey ?? 'manual'}`} sx={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', gap: 1, py: 0.15 }}>
            <Box sx={{ minWidth: 0, display: 'flex', alignItems: 'baseline', gap: 0.5 }}>
              <Typography variant="caption" fontWeight={600} sx={{ fontSize: '0.7rem', flexShrink: 0 }}>{s.label}</Typography>
              {s.name && (
                <Typography variant="caption" color="text.secondary" title={s.name} sx={{ fontSize: '0.64rem', minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  {s.name}
                </Typography>
              )}
            </Box>
            <Typography variant="caption" fontWeight={800} sx={{ fontSize: '0.72rem', color, flexShrink: 0 }}>
              {Math.round(s.percent)}%
            </Typography>
          </Box>
        );
      })}
    </Box>
  );
};

// ---------------------------------------------------------------------------
// Header capacity-chip color
// ---------------------------------------------------------------------------

/** Chip colour for the WEEK's percent-of-week busy (Q5B, 100% = every day busy). */
function weekChipColor(pct: number): 'default' | 'success' | 'warning' | 'error' {
  if (pct <= 0)   return 'default';
  if (pct <= 70)  return 'success';
  if (pct <= 100) return 'warning';
  return 'error';
}

// ---------------------------------------------------------------------------
// Main component
// ---------------------------------------------------------------------------

export const EmployeeWeekCellPopup: React.FC<EmployeeWeekCellPopupProps> = ({
  anchorEl,
  userKey,
  userName,
  weekStart,
  totalPercent,
  busyDays,
  workingDays,
  assignments,
  isLoading,
  error,
  onClose,
  onAddAssignment,
  readOnly = false,
  clickedDay = null,
  dayPercent = 0,
  dayTaskCount,
  dayAbsent,
  daySoftPercent = 0,
  softSources = [],
  holidays,
  weekAllocationPercent,
  sourceAllocations,
}) => {
  const theme = useTheme();
  const isOpen = Boolean(anchorEl);

  // Day-scoped by default when a day was clicked; the user can expand to the week.
  // Re-arm to the day view every time a NEW cell opens the popup (anchor changes).
  const [showWeek, setShowWeek] = useState(false);
  useEffect(() => {
    if (anchorEl) setShowWeek(!clickedDay); // no day → week view; day → day view
  }, [anchorEl, clickedDay]);

  const dayMode = Boolean(clickedDay) && !showWeek;

  // Day view filters the week's assignments to those active on the clicked day.
  const dayAssignments = clickedDay
    ? assignments.filter((a) => assignmentCoversDay(a, clickedDay))
    : assignments;
  const shownAssignments = dayMode ? dayAssignments : assignments;
  const hasSoft = daySoftPercent > 0 || softSources.length > 0;

  const headerLabel = dayMode && clickedDay ? formatDayLabel(clickedDay) : formatWeekLabel(weekStart);
  // Day header → the day's STATE (free / busy · N tests / day off / conflict —
  // amber when the task stack exceeds the Q1B threshold). Week header → the
  // Q5B percent-of-week (busyDays ÷ workingDays; legacy-sum fallback).
  const dayState = personDayState(dayTaskCount, dayAbsent, dayPercent);
  // BUG (v1.0.09, blank Scenario Planning page): this ran EAGERLY on every
  // render — including the CLOSED popover, whose state carries the sentinel
  // weekStart '' — and workingDaysInWeek('') used to throw "Invalid time
  // value", white-screening the app (no error boundary at the time). Guard on
  // weekStart so a closed popover never does week math; the helpers are also
  // hardened, but this keeps the intent explicit.
  const weekPct = weekStart
    ? weekPercentFromCell(
        { totalPercent, busyDays, workingDays },
        workingDaysInWeek(weekStart, holidays),
      )
    : 0;
  const dayChipLabel = personDayStateLabel(dayState, dayTaskCount ?? 0);
  const dayChipColorName: 'default' | 'success' | 'warning' | 'error' =
    dayState === 'conflict'
      ? 'error'
      : dayState === 'absent'
        ? 'default'
        : dayState === 'busy'
          ? (isTaskCountAmber(dayTaskCount ?? 0) ? 'warning' : 'success')
          : 'default';
  const chipColor = dayMode ? dayChipColorName : weekChipColor(weekPct);

  return (
    <Popover
      open={isOpen}
      anchorEl={anchorEl}
      onClose={onClose}
      anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      transformOrigin={{ vertical: 'top', horizontal: 'center' }}
      disableScrollLock
      PaperProps={{
        elevation: 4,
        sx: {
          borderRadius: '8px',
          border: `1px solid ${theme.border.color}`,
          width: 420,
          maxWidth: '96vw',
          overflow: 'hidden',
        },
      }}
    >
      {/* ── Header ─────────────────────────────────────────────────────── */}
      <Box
        sx={{
          px: 2,
          py: 1.25,
          backgroundColor: theme.table.headerBg,
          borderBottom: `1px solid ${theme.border.color}`,
          display: 'flex',
          alignItems: 'center',
          gap: 1,
          flexWrap: 'wrap',
        }}
      >
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography variant="subtitle2" fontWeight={700} noWrap sx={{ fontSize: '0.82rem' }}>
            {userName}
          </Typography>
          <Typography variant="caption" color="text.secondary" display="block" sx={{ fontSize: '0.68rem' }}>
            {headerLabel}
          </Typography>
          {/* Staffing Allocation % (D5) — the week's utilization + band. The
              per-source breakdown is the assignment list below (week view). */}
          {typeof weekAllocationPercent === 'number' && weekAllocationPercent > 0 && (
            <Typography
              variant="caption"
              display="block"
              sx={{
                fontSize: '0.66rem',
                fontWeight: 700,
                mt: 0.15,
                color:
                  allocationBand(weekAllocationPercent) === 'over' ? theme.schedule.allocOver
                    : allocationBand(weekAllocationPercent) === 'at' ? theme.schedule.allocAt
                      : allocationBand(weekAllocationPercent) === 'healthy' ? theme.schedule.allocHealthy
                        : theme.schedule.allocUnder,
              }}
            >
              Allocation {Math.round(weekAllocationPercent)}% — {allocationBandLabel(allocationBand(weekAllocationPercent))}
            </Typography>
          )}
        </Box>
        {(dayMode ? dayState !== 'free' : weekPct > 0) && (
          <Chip
            label={dayMode ? dayChipLabel : `${weekPct}%`}
            size="small"
            color={chipColor}
            sx={{ height: 22, fontSize: '0.68rem', fontWeight: 700 }}
          />
        )}
      </Box>

      {/* ── Day/Week toggle (only when a day was clicked) ─────────────────── */}
      {clickedDay && (
        <Box
          sx={{
            px: 2,
            py: 0.5,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 1,
            backgroundColor: theme.table.stripeBg,
            borderBottom: `1px solid ${theme.border.color}`,
          }}
        >
          <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.66rem' }}>
            {dayMode ? 'Showing this day' : 'Showing the whole week'}
          </Typography>
          <Button
            size="small"
            variant="text"
            startIcon={dayMode ? <CalendarViewWeekIcon sx={{ fontSize: '0.85rem' }} /> : <TodayIcon sx={{ fontSize: '0.85rem' }} />}
            onClick={() => setShowWeek((v) => !v)}
            sx={{ fontSize: '0.68rem', textTransform: 'none', minWidth: 0, py: 0.1 }}
          >
            {dayMode ? 'Whole week' : 'This day'}
          </Button>
        </Box>
      )}

      {/* ── Body ─────────────────────────────────────────────────────────── */}
      <Box sx={{ px: 2, py: 1, maxHeight: 360, overflowY: 'auto' }}>

        {/* Loading */}
        {isLoading && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, py: 2 }}>
            <CircularProgress size={14} thickness={4} />
            <Typography variant="caption" color="text.secondary">
              Loading assignments…
            </Typography>
          </Box>
        )}

        {/* Error */}
        {!isLoading && error && (
          <Alert severity="error" sx={{ py: 0.5, fontSize: '0.72rem', my: 0.5 }}>
            {error}
          </Alert>
        )}

        {/* Empty (no hard load in this scope) — only when there's no soft block either. */}
        {!isLoading && !error && shownAssignments.length === 0 && !hasSoft && (
          <Box sx={{ py: 2, textAlign: 'center' }}>
            <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.78rem' }}>
              {dayMode ? 'Nothing scheduled this day.' : 'No assignments this week.'}
            </Typography>
          </Box>
        )}

        {/* Per-source allocation breakdown (D6) — week view only: which WO /
            quote contributes what % of the week's utilization. */}
        {!isLoading && !error && !dayMode && sourceAllocations && sourceAllocations.length > 0 && (
          <SourceBreakdownBlock sources={sourceAllocations} />
        )}

        {/* Assignment rows */}
        {!isLoading && !error && shownAssignments.length > 0 && (
          <Box>
            {shownAssignments.map((a) => (
              <AssignmentListRow key={a.assignmentKey} assignment={a} weekStart={weekStart} mode={dayMode ? 'day' : 'week'} holidays={holidays} />
            ))}
          </Box>
        )}

        {/* Cross-scenario soft hold block (shown in either view when present).
            P2-6: presence language only — no stored-percent number. */}
        {!isLoading && !error && hasSoft && (
          <SoftHoldBlock heldToday={dayMode && daySoftPercent > 0} sources={softSources} />
        )}
      </Box>

      {/* ── Footer ─────────────────────────────────────────────────────── */}
      {!readOnly && (
        <>
          <Divider />
          <Box
            sx={{
              px: 2,
              py: 1,
              display: 'flex',
              justifyContent: 'flex-end',
              backgroundColor: theme.table.stripeBg,
            }}
          >
            <Button
              size="small"
              variant="outlined"
              startIcon={<AddIcon sx={{ fontSize: '0.9rem' }} />}
              onClick={() => {
                onAddAssignment?.(userKey, weekStart);
              }}
              sx={{ fontSize: '0.75rem', textTransform: 'none', height: 30 }}
            >
              Add Assignment
            </Button>
          </Box>
        </>
      )}
    </Popover>
  );
};
